import React from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import "./index.css";
import App from "./App";
import { registerPWA } from "./pwa";

// PII scrubbing: booking flows put customer emails/phones into inputs, URLs
// and breadcrumbs. Strip them before anything leaves the browser — we hold
// customer data under our privacy policy; Sentry shouldn't accumulate it.
const PII_EMAIL_RE = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
const PII_PHONE_RE = /\+?\d[\d\s\-().]{8,}\d/g;
function scrubString(v) {
  return typeof v === "string"
    ? v.replace(PII_EMAIL_RE, "[email]").replace(PII_PHONE_RE, "[phone]")
    : v;
}
function scrubEvent(event) {
  try {
    // Only report the real production site — preview channels and local
    // builds are test noise (they polluted Sentry during PWA testing).
    const host = window.location.hostname;
    if (host !== "bookyara.com" && host !== "www.bookyara.com") return null;

    delete event.user;
    if (event.message) event.message = scrubString(event.message);
    if (event.request?.url) event.request.url = scrubString(event.request.url);
    event.exception?.values?.forEach((ex) => { ex.value = scrubString(ex.value); });
    event.breadcrumbs?.forEach((b) => {
      b.message = scrubString(b.message);
      if (b.data) {
        for (const k of Object.keys(b.data)) b.data[k] = scrubString(b.data[k]);
      }
    });
  } catch { /* never block the report over scrubbing */ }
  return event;
}

if (process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.NODE_ENV,
    // Only send errors in production — not during local dev
    enabled: process.env.NODE_ENV === "production",
    beforeSend: scrubEvent,
    tracesSampleRate: 0.2,
    replaysOnErrorSampleRate: 1.0,
  });
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={
      <div className="flex flex-col min-h-screen items-center justify-center px-4 text-center bg-surface-50">
        <div className="text-4xl mb-4">😕</div>
        <h2 className="font-semibold text-2xl text-ink-900 mb-2">Something went wrong</h2>
        <p className="text-ink-400 text-sm font-sans mb-6">We've been notified and are looking into it.</p>
        <button onClick={() => window.location.reload()} className="btn-primary">Reload page</button>
      </div>
    }>
      <App />
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);

// PWA: installable app shell + offline. Isolated in ./pwa, easy to remove.
registerPWA();
